import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';

/**
 * QRCodeDisplay - Componente para mostrar QR y enlace directo a un evento
 * @param {string} url - URL completa del evento en el microsite
 * @param {string} eventTitle - Título del evento para la etiqueta
 * @param {string} brandColor - Color de marca del organizador (opcional)
 */
const QRCodeDisplay = ({ url, eventTitle, brandColor = '#72c02c' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success('¡Enlace copiado!');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('Error al copiar el enlace');
        }
    };

    const handleDownloadQR = () => {
        const svg = document.getElementById('qr-code-svg');
        if (!svg) return;

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            
            const pngFile = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.download = `qr-${eventTitle.replace(/\s+/g, '-').toLowerCase()}.png`;
            downloadLink.href = pngFile;
            downloadLink.click();
        };

        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    };

    return (
        <div className="flex flex-col items-center gap-6 p-6 bg-white rounded-2xl border border-gray-100">
            {/* QR Code */}
            <div className="p-4 bg-white rounded-xl shadow-inner border border-gray-50">
                <QRCodeSVG
                    id="qr-code-svg"
                    value={url}
                    size={200}
                    level="H"
                    includeMargin={true}
                    fgColor="#1e293b"
                    bgColor="#ffffff"
                />
            </div>

            {/* Título del evento */}
            <div className="text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">
                    Evento
                </p>
                <p className="font-bold text-gray-900 text-lg">
                    {eventTitle}
                </p>
            </div>

            {/* URL preview */}
            <div className="w-full bg-gray-50 rounded-xl p-3 border border-gray-100">
                <p className="text-xs text-gray-500 break-all text-center font-mono">
                    {url}
                </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 w-full">
                <button
                    onClick={handleCopyLink}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                        copied
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                    }`}
                >
                    {copied ? (
                        <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Copiado
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                            </svg>
                            Copiar Enlace
                        </>
                    )}
                </button>

                <button
                    onClick={handleDownloadQR}
                    className="flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 text-white shadow-lg hover:scale-105"
                    style={{ backgroundColor: brandColor }}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Descargar QR
                </button>
            </div>

            {/* Help text */}
            <p className="text-xs text-gray-400 text-center leading-relaxed">
                Comparte este QR o enlace con tus jugadores para que se inscriban directamente desde tu espacio.
            </p>
        </div>
    );
};

export default QRCodeDisplay;
